import {
  type CreateSenderPayload,
  isSmsSender,
  type Sender,
  type SenderMethod,
  type UpdateSenderPayload,
} from '../../types/senders'

export interface SenderFormData {
  method: SenderMethod
  nickname: string
  fromEmail: string
  fromName: string
  replyToEmail: string
  replyToName: string
  phoneNumber: string
  inboundApp: '' | 'schedule'
  active: boolean
}
export const EMPTY_SENDER_FORM: SenderFormData = {
  method: 'EMAIL',
  nickname: '',
  fromEmail: '',
  fromName: '',
  replyToEmail: '',
  replyToName: '',
  phoneNumber: '',
  inboundApp: '',
  active: true,
}
export function senderToFormData(sender: Sender): SenderFormData {
  return isSmsSender(sender)
    ? {
        ...EMPTY_SENDER_FORM,
        method: 'SMS',
        nickname: sender.nickname,
        phoneNumber: sender.phoneNumber,
        inboundApp: sender.inboundApp ?? '',
        active: sender.active,
      }
    : {
        ...EMPTY_SENDER_FORM,
        nickname: sender.nickname,
        fromEmail: sender.fromEmail,
        fromName: sender.fromName,
        replyToEmail: sender.replyToEmail,
        replyToName: sender.replyToName ?? '',
      }
}
export function isSenderFormValid(data: SenderFormData): boolean {
  if (!data.nickname.trim()) {
    return false
  }
  return data.method === 'SMS'
    ? /^\+[1-9]\d{7,14}$/.test(data.phoneNumber.trim())
    : Boolean(data.fromEmail.trim() && data.fromName.trim() && data.replyToEmail.trim())
}
export function senderCreateReady(
  data: SenderFormData,
  saving: boolean,
  loadingAddress: boolean,
  addressMissing: boolean,
) {
  return isSenderFormValid(data) && !saving && (data.method === 'SMS' || (!loadingAddress && !addressMissing))
}
export function createSenderPayload(data: SenderFormData): CreateSenderPayload {
  return data.method === 'SMS'
    ? {
        method: 'SMS',
        nickname: data.nickname.trim(),
        phoneNumber: data.phoneNumber.trim(),
        inboundApp: data.inboundApp || null,
      }
    : {
        nickname: data.nickname.trim(),
        fromEmail: data.fromEmail.trim(),
        fromName: data.fromName.trim(),
        replyToEmail: data.replyToEmail.trim(),
        replyToName: data.replyToName.trim() || null,
      }
}
export function diffSenderPayload(data: SenderFormData, sender: Sender): UpdateSenderPayload {
  if (isSmsSender(sender)) {
    return {
      ...(data.nickname.trim() !== sender.nickname ? { nickname: data.nickname.trim() } : {}),
      ...((data.inboundApp || null) !== sender.inboundApp ? { inboundApp: data.inboundApp || null } : {}),
      ...(data.active !== sender.active ? { active: data.active } : {}),
    }
  }
  const payload: Record<string, string | null> = {}
  for (const field of ['nickname', 'fromEmail', 'fromName', 'replyToEmail'] as const) {
    if (data[field].trim() !== sender[field]) {
      payload[field] = data[field].trim()
    }
  }
  const replyToName = data.replyToName.trim() || null
  if (replyToName !== (sender.replyToName ?? null)) {
    payload.replyToName = replyToName
  }
  return payload
}
